<script setup>
import { useRoute } from 'vue-router'
import { useLayoutStore } from '@/stores/layout.js'
import { useNavigationStore } from '@/stores/navigation.js'
import { useAuthUserStore } from '@/stores/auth.js'
import { useAdminSummaryStore } from '@/stores/adminSummary.js'
import { onMounted } from 'vue'
import Skeleton from '@/volt/Skeleton.vue';
import Button from '@/volt/Button.vue';

import Logo from '@/components/ui/Logo.vue';

const layoutStore = useLayoutStore();
const navigation = useNavigationStore();
const auth = useAuthUserStore();
const route = useRoute();
const adminSummary = useAdminSummaryStore();

// An item with `badge: 'reviews'` (Mapping) shows how many reviews wait; only admins can read that count.
onMounted(() => { if (auth.getIsAdmin) adminSummary.refresh(); });
const badgeOf = item => (item.badge === 'reviews' ? adminSummary.count : 0);

function matchesRoute(node) {
    if (!node.route || node.route !== route.name) return false;
    if (!node.params) return true;
    return Object.entries(node.params).every(
        ([key, value]) => String(route.params[key]) === String(value)
    );
}

function hasActiveChild(node) {
    if (!node.items?.length) return false;
    return node.items.some((child) => matchesRoute(child) || hasActiveChild(child));
}

</script>

<template>
    <button
        v-if="layoutStore.mobileSidebarVisible"
        type="button"
        class="fixed inset-0 z-[55] bg-navy-900/40 xl:hidden"
        aria-label="Close navigation"
        @click="layoutStore.closeMobileSidebar"
    />
    <div id="application-sidebar" 
        :class="layoutStore.mobileSidebarVisible ? 'block translate-x-0' : 'hidden -translate-x-full'"
        class="[--auto-close:xl]
            transition-all duration-300 transform
            h-full
            fixed inset-y-0 start-0 z-[60]
            border-e border-gray-200
            xl:block xl:translate-x-0 xl:end-auto xl:bottom-0
            bg-navy-900
            dark:border-surface-800 dark:text-surface-300"
        role="dialog"
        tabindex="-1" 
        aria-label="Sidebar"
    >
        <!--  @mouseover="layoutStore.minimisedSidebar = false" @mouseleave="layoutStore.minimisedSidebar = true" -->
        <div 
            :class="layoutStore.minimisedSidebar ? 'w-72 xl:hidden' : 'w-72'"
            class="bg-navy-900 h-screen border-r border-surface dark:border-surface-800 flex flex-col select-none absolute left-0 top-0">
            <div class="px-6 py-4 flex items-center justify-between">
                <Logo />
                <Button
                    icon="pi pi-times"
                    text
                    rounded
                    class="xl:hidden text-surface-300 hover:text-surface-0"
                    aria-label="Close navigation"
                    @click="layoutStore.closeMobileSidebar"
                />
            </div>
            <div class="overflow-y-auto text-sm">
                <ul v-if="navigation.loading" class="list-none p-2 m-0 space-y-1">
                    <li v-for="i in 4" :key="i" class="px-4 py-2">
                        <Skeleton height="0.65rem" width="40%" class="mb-4 opacity-30" />
                        <div class="space-y-2 pl-1">
                            <div v-for="j in 3" :key="j" class="flex items-center gap-3 py-1">
                                <Skeleton shape="circle" size="1rem" class="opacity-30 shrink-0" />
                                <Skeleton height="0.65rem" :width="(60 + (j * 7)) + '%'" class="opacity-30" />
                            </div>
                        </div>
                    </li>
                </ul>
                <ul v-else class="list-none p-2 m-0">
                    <li v-for="group in navigation.getNodes" :key="group.id">
                        <div v-if="group.title" class="p-4 text-surface-600 font-medium border-t border-surface-800 dark:border-surface-800">
                        {{ group.title.toUpperCase() }}
                        </div>
                        <ul class="list-none py-0 pl-0 pr-0 m-0">
                            <li v-for="item in group.items" :key="item.id">
                                <RouterLink
                                    v-if="!item.items || item.items.length === 0"
                                    :to="{ name: item.route, params: item.params || null}"
                                    @click="layoutStore.closeMobileSidebar"
                                    active-class="bg-navy-800 hover:border-transparent!"
                                    class="flex items-center cursor-pointer py-3 pl-[14px] pr-4 border-l-2 border-transparent text-surface-300 hover:bg-navy-800 hover:border-primary-500 rounded-border transition-colors duration-150"
                                    >
                                        <i :class="item.icon + ' mr-2'" />
                                        <span class="font-medium">{{ item.title }}</span>
                                        <span
                                            v-if="badgeOf(item) > 0"
                                            class="ml-auto min-w-5 rounded-full bg-primary-500 px-1.5 text-center text-xs font-semibold leading-5 text-white"
                                            :data-testid="`nav-badge-${item.id}`"
                                        >{{ badgeOf(item) }}</span>
                                </RouterLink>
                                <a
                                v-if="item.items && item.items.length"
                                v-styleclass="{
                                    selector: '@next',
                                    enterFromClass: 'hidden',
                                    enterActiveClass: 'animate-slidedown',
                                    leaveToClass: 'hidden',
                                    leaveActiveClass: 'animate-slideup'
                                }"
                                :class="hasActiveChild(item) ? 'bg-navy-800' : ''"
                                class="flex items-center cursor-pointer py-3 pl-[14px] pr-4 border-l-2 border-transparent text-surface-300 hover:bg-navy-800 hover:border-primary-500 rounded-border transition-colors duration-150"
                                >
                                    <i :class="item.icon + ' mr-2'" />
                                    <span class="font-medium">{{ item.title }}</span>
                                    <i class="pi pi-chevron-down ml-auto" />
                                </a>
                                <ul v-if="item.items && item.items.length" :class="hasActiveChild(item) ? '' : 'hidden'" class="list-none py-0 pl-4 pr-0 m-0 overflow-y-hidden transition-all duration-[400ms] ease-in-out">
                                    <li v-for="subItem in item.items" :key="subItem.id">
                                        <RouterLink
                                        v-if="!subItem.items || subItem.items.length === 0"
                                        :to="{ name: subItem.route, params: subItem.params || null}"
                                        @click="layoutStore.closeMobileSidebar"
                                        active-class="bg-navy-800 hover:border-transparent!"
                                        class="flex items-center cursor-pointer py-3 pl-[14px] pr-4 border-l-2 border-transparent text-surface-300 hover:bg-navy-800 hover:border-primary-500 rounded-border transition-colors duration-150"
                                        >
                                            <i :class="subItem.icon + ' mr-2'" />
                                            <span class="font-medium">{{ subItem.title }}</span>
                                        </RouterLink>
                                        <a
                                        v-if="subItem.items && subItem.items.length"
                                        v-styleclass="{
                                            selector: '@next',
                                            enterFromClass: 'hidden',
                                            enterActiveClass: 'animate-slidedown',
                                            leaveToClass: 'hidden',
                                            leaveActiveClass: 'animate-slideup'
                                        }"
                                        :class="hasActiveChild(subItem) ? 'bg-navy-800' : ''"
                                        class="flex items-center cursor-pointer py-3 pl-[14px] pr-4 border-l-2 border-transparent text-surface-300 hover:bg-navy-800 hover:border-primary-500 rounded-border transition-colors duration-150"
                                        >
                                            <i :class="subItem.icon + ' mr-2'" />
                                            <span class="font-medium">{{ subItem.title }}</span>
                                            <i class="pi pi-chevron-down ml-auto" />
                                        </a>
                                        <ul v-if="subItem.items && subItem.items.length" :class="hasActiveChild(subItem) ? '' : 'hidden'" class="list-none py-0 pl-4 pr-0 m-0 overflow-y-hidden transition-all duration-[400ms] ease-in-out">
                                            <li v-for="subSubItem in subItem.items" :key="subSubItem.id">
                                                <a
                                                class="flex items-center cursor-pointer py-3 pl-[14px] pr-4 border-l-2 border-transparent text-surface-300 hover:bg-navy-800 hover:border-primary-500 rounded-border transition-colors duration-150"
                                                >
                                                    <i :class="subSubItem.icon + ' mr-2'" />
                                                    <span class="font-medium">{{ subSubItem.title }}</span>
                                                </a>
                                            </li>
                                        </ul>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </li>
                </ul>
            </div>
            <div class="p-2 mt-auto border-t border-surface-800">
                <a class="flex items-center cursor-pointer p-4 text-surface-300 hover:bg-surface-800 rounded-border transition-colors duration-150">
                    <img :src="'https://ui-avatars.com/api/?rounded=true&background=random&name=' + auth.getAuthenticatedUser.name" class="mr-2 w-7 h-7" />
                    <span class="font-medium">{{ auth.getAuthenticatedUser.name }}</span>
                </a>
            </div>
        </div>
    </div>
</template>

<style lang="css" scoped>
@reference "@/style.css";

/* width */
::-webkit-scrollbar {
  width: 8px;
}

/* Track */
::-webkit-scrollbar-track {
  @apply bg-surface-800
}
 
/* Handle */
::-webkit-scrollbar-thumb {
  @apply bg-surface-700 rounded-xl
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
  @apply bg-surface-500
}

</style>
